import { roleDisplayName, tt } from '@agos/shared'
import { BUTTON_PRIMARY, cn, Spinner } from '@agos/ui'
import { Plus } from 'lucide-react'
import { useState } from 'react'

import { roleRefusalCode, useRolesOverview, type RoleView } from '../../roles/roles-api'
import { edgesOfViewer, edgesText, orderRoles, type Navigate, type Place, type RoleTicks } from '../role-model'
import { NotManaged, TabReadFailed } from '../TabReadFailed'
import { DeleteRoleDialog } from './DeleteRoleDialog'
import { NewRoleDialog, type NewRoleSeed } from './NewRoleDialog'
import { RoleCard } from './RoleCard'
import { RoleEditor } from './RoleEditor'

/**
 * *Role* - the default tab: a card per role, and a role's editor at `?role=key` (design §2.9).
 *
 * **Unsaved ticks are kept per role while the tab is open**, as the old matrix kept them per role tab: moving to
 * another role and back must not throw a half-made change away, and the card says which roles hold one. Each set is
 * kept with the version it was ticked on (`RoleTicks`), so a re-read of the overview never rebases it.
 */
export function RolesTab({ mainAdmin, place, navigate }: { mainAdmin: boolean; place: Place; navigate: Navigate }) {
  const overview = useRolesOverview()
  const [local, setLocal] = useState<Record<string, RoleTicks>>({})
  const [creating, setCreating] = useState<NewRoleSeed | null>(null)
  const [deleting, setDeleting] = useState<RoleView | null>(null)
  const [renaming, setRenaming] = useState('')

  const setLocalFor = (key: string, next: RoleTicks | null) =>
    setLocal((current) => {
      const copy = { ...current }
      if (next === null) delete copy[key]
      else copy[key] = next
      return copy
    })

  if (overview.isError) {
    if (roleRefusalCode(overview.error) === 'ROLE_NOT_MANAGED') return <NotManaged />
    return <TabReadFailed what="roles" retry={() => void overview.refetch()} />
  }

  if (overview.isLoading || overview.data === undefined) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    )
  }

  const data = overview.data
  const roles = orderRoles(data.roles)
  const open = roles.find((role) => role.key === place.role && role.key !== 'main_admin')

  const dialogs = (
    <>
      {creating !== null && (
        <NewRoleDialog
          seed={creating}
          overview={data}
          onClose={() => setCreating(null)}
          onCreated={(role) => {
            setCreating(null)
            navigate({ role: role.key })
          }}
        />
      )}
      {deleting !== null && (
        <DeleteRoleDialog
          role={deleting}
          overview={data}
          onClose={() => setDeleting(null)}
          onDeleted={() => {
            setLocalFor(deleting.key, null)
            setDeleting(null)
            navigate({ role: '' })
          }}
        />
      )}
    </>
  )

  if (open !== undefined) {
    return (
      <>
        {/* Keyed by the role, so a stale banner, an open confirmation or a half-settled preview belongs to the role it
            was raised on; the unsaved ticks live here, per role, and survive the switch. */}
        <RoleEditor
          key={open.key}
          overview={data}
          role={open}
          roles={roles}
          mainAdmin={mainAdmin}
          local={local[open.key]}
          setLocal={(next) => setLocalFor(open.key, next)}
          locals={local}
          renaming={renaming === open.key}
          setRenaming={(renamingNow) => setRenaming(renamingNow ? open.key : '')}
          navigate={navigate}
          onDuplicate={() => setCreating(duplicateSeed(open))}
          onDelete={() => setDeleting(open)}
          reload={() => overview.refetch()}
        />
        {dialogs}
      </>
    )
  }

  const scope = edgesText(edgesOfViewer(roles, data.you.roleKey), roles)

  return (
    <div className="space-y-4" data-roles-tab>
      <div className="flex flex-wrap items-center justify-between gap-3" data-roles-action-line>
        {mainAdmin ? (
          <button
            type="button"
            onClick={() => setCreating({ kind: 'blank' })}
            className={cn('flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm', BUTTON_PRIMARY)}
            data-role-new
          >
            <Plus className="w-4 h-4" />
            {tt('Nová rola')}
          </button>
        ) : (
          <p className="text-sm" data-roles-managed-scope>
            <strong>{tt('Spravujete')}:</strong> {scope === '' ? tt('zatiaľ žiadnu rolu') : scope}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-role-cards>
        {roles.map((role) => (
          <RoleCard
            key={role.key}
            role={role}
            overview={data}
            roles={roles}
            mainAdmin={mainAdmin}
            unsaved={local[role.key] !== undefined}
            onEdit={() => navigate({ role: role.key })}
            onPeople={() => navigate({ tab: 'people', role: role.key })}
            onRename={() => {
              setRenaming(role.key)
              navigate({ role: role.key })
            }}
            onDuplicate={() => setCreating(duplicateSeed(role))}
            onDelete={() => setDeleting(role)}
          />
        ))}
      </div>

      {dialogs}
    </div>
  )
}

/** *Duplikovať* - a new custom role with the same base, starting from a copy of this one's rights. */
function duplicateSeed(role: RoleView): NewRoleSeed {
  return {
    kind: 'copy',
    fromKey: role.key,
    name: `${roleDisplayName(role)} - ${tt('kópia')}`,
    baseRole: role.baseRole,
  }
}
